
import { LoaderFunctionArgs, useLoaderData, useLocation } from "react-router-dom";
import { ITEM_REPORT } from "./fragment";
import { formatData, colDefs, getGroupedColDefs, getAutoGroupColumnDef, formatPivotData, generatePivotColDefs, PivotRow } from "./helpers";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { userStore } from "../../../../store/user";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { ItemReportRow } from "./type";
import React, { useEffect, useRef, useState } from "react";
import { eventStore } from "../../../../store/event";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { SecondaryButton, ToggleButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { CsvCell, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";
import CustomSelect from "../../../../components/customSelect";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

export const itemReportTicketingLoader = async ({ request }: LoaderFunctionArgs) => {
  // RP-1937: no filter in the address (e.g. a sidebar click). Don't load every sale ever;
  // ReportFilters puts its dates back in the address and this loader runs again.
  if (!new URL(request.url).searchParams.has("where")) return [];
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const { eventId } = getEventState();
  const user = getState().user;

  const search = new URL(request.url);
  const where = search.searchParams.get("where");
  let whereVariable: any = where ? JSON.parse(where) : {};
  if (organizationId && organizationId !== 0) {
    if (user?.vendors) {
      whereVariable = {
        ...whereVariable,
        vendor_id: {
          _in: user?.vendors,
        },
      };
    }

    whereVariable = {
      ...whereVariable,
      organization_id: {
        _eq: organizationId,
      },
    };
  }

  if (eventId) {
    whereVariable = {
      ...whereVariable,
      event_id: {
        _eq: eventId,
      },
    };
  } else if (user?.events) {
    whereVariable = {
      ...whereVariable,
      event_id: {
        _in: user?.events,
      },
    };
  }
  const { data } = await client.query({
    query: ITEM_REPORT,
    variables: { where: whereVariable },
  });
  return data.reports_product_mix_ticketing || [];
};

type DisplayType = 'by_item' | 'by_day_by_item' | 'by_location_by_item' | 'by_day_by_location_by_item' | 'by_hour_by_item_pivot';

const displayTypeOptions = [
  { value: 'by_item', label: 'By Item' },
  { value: 'by_day_by_item', label: 'By Day → By Item' },
  { value: 'by_location_by_item', label: 'By Location → By Item' },
  { value: 'by_day_by_location_by_item', label: 'By Day → By Location → By Item' },
  { value: 'by_hour_by_item_pivot', label: 'By Hour → By Item (Pivot)' },
];

const BarChartComponent = ({
  rows,
  selectedDataKey,
  displayType,
}: {
  rows: ItemReportRow[];
  selectedDataKey: string;
  displayType: string;
}) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-4">
        <div className="text-gray-500 text-sm">No data to display</div>
      </div>
    );
  }

  const getXAxisKey = () => {
    if (displayType === 'by_day_by_item') return 'transaction_date';
    if (displayType === 'by_location_by_item') return 'location_name';
    if (displayType === 'by_day_by_location_by_item') return 'transaction_date';
    return 'pos_name';
  };

  const dataWithLabels = rows.map(row => {
    let label = '';
    const dateOnly = row.transaction_date ? row.transaction_date.split('T')[0] : row.transaction_date;
    if (displayType === 'by_day_by_item') {
      label = `${dateOnly} - ${row.pos_name}`;
    } else if (displayType === 'by_location_by_item') {
      label = `${row.location_name || 'Unknown'} - ${row.pos_name}`;
    } else if (displayType === 'by_day_by_location_by_item') {
      label = `${dateOnly} - ${row.location_name || 'Unknown'} - ${row.pos_name}`;
    } else {
      label = row.pos_name;
    }
    return { ...row, chartLabel: label };
  });

  const filteredData = dataWithLabels.filter(item => item !== null && item !== undefined);
  const sortedData = filteredData.sort((a, b) => {
    const key = selectedDataKey as keyof ItemReportRow;
    const aValue = a[key] as number;
    const bValue = b[key] as number;
    return bValue - aValue;
  });
  const customTickFormatter = (value: number) => {
    return value.toString();
  };

  const isTotalUnitsSold = selectedDataKey === "total_units_sold";
  const limitedData = sortedData.slice(0, 20);
  const usdFormatter = (value: number) => {
    return `$${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
  };
  function formatDataKey(key: string) {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  const maxProductTypeLength = Math.max(
    ...limitedData.map((item) => (item.chartLabel ? item.chartLabel.length : 0))
  );
  const angleInRadians = (Math.abs(-30) * Math.PI) / 180;
  const textHeight = Math.sin(angleInRadians) * maxProductTypeLength * 10;
  const xAxisHeight = textHeight > 50 ? textHeight : 50;
  return (
    <div className="flex bg-white rounded-md flex-row items-center w-full">
      <div className="overflow-x-auto">
        <div className="flex flex-col">
          <div className="inline-block min-w-full align-middle">
            <BarChart
              width={1200}
              height={350 + xAxisHeight}
              data={limitedData}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="chartLabel"
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
                {...(sortedData.length > 5 && {
                  interval: 0,
                  angle: -35,
                  textAnchor: "end",
                  height: xAxisHeight,
                })}
              />
              <YAxis
                width={100}
                tickFormatter={
                  isTotalUnitsSold ? customTickFormatter : usdFormatter
                }
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
              />
              <Tooltip
                contentStyle={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#4a5568",
                }}
                formatter={
                  isTotalUnitsSold ? customTickFormatter : usdFormatter
                }
              />
              <Bar
                dataKey={selectedDataKey}
                fill="#B71C1C"
                name={formatDataKey(selectedDataKey)}
              />
            </BarChart>
          </div>
        </div>
      </div>
    </div>
  );
};
export const ItemTicketingReport = () => {
  const data = useLoaderData() as ItemReportRow[];
  const [displayType, setDisplayType] = useState<DisplayType>('by_item');
  // RP-2560: Item Category filter. product_type on this report IS the ticketing
  // item's category (items_ticketing.category). Options grow as data loads, so
  // picking a category doesn't shrink the list down to just that one.
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  useEffect(() => {
    const found = (data || []).map((row) => row.product_type).filter((c): c is string => !!c);
    if (!found.length) return;
    setCategoryOptions((prev) =>
      Array.from(new Set([...prev.map((o) => o.value), ...found]))
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .map((c) => ({ label: c, value: c }))
    );
  }, [data]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const isPivot = displayType === 'by_hour_by_item_pivot';
  const formattedData = isPivot ? [] : formatData(data, displayType);
  const pivotResult = isPivot ? formatPivotData(data) : { rows: [], totalRow: [], itemNames: [] };

  const chartRows = formattedData.filter((row) => row.pos_name !== "Total" && !row.isGroupRow);

  const flattenData = (data: ItemReportRow[]): ItemReportRow[] => {
    const result: ItemReportRow[] = [];
    data.forEach(row => {
      if (row.pos_name !== "Total") {
        result.push(row);
        if (row.isGroupRow && row.detailRows && expandedRows.has(row.id)) {
          row.detailRows.forEach(detailRow => {
            result.push(detailRow);
            if (detailRow.isGroupRow && detailRow.detailRows && expandedRows.has(detailRow.id)) {
              detailRow.detailRows.forEach(nestedDetailRow => {
                result.push(nestedDetailRow);
              });
            }
          });
        }
      }
    });
    return result;
  };

  const tableRows = isPivot
    ? pivotResult.rows
    : displayType === 'by_item'
      ? formattedData.filter((row) => row.pos_name !== "Total")
      : flattenData(formattedData);
  const totalRow = isPivot
    ? pivotResult.totalRow
    : formattedData.filter((row) => row.pos_name === "Total");

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const currentColDefs = isPivot
    ? generatePivotColDefs(pivotResult.itemNames)
    : displayType === 'by_item'
      ? colDefs
      : getGroupedColDefs(displayType, expandedRows, toggleRowExpansion);
  const [selectedDataKey, setSelectedDataKey] = useState("total_net_sales");
  const handleDataKeyChange = (newDataKey: string) => {
    setSelectedDataKey(newDataKey);
  };
  const location = useLocation();
  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `TicketingItemReport_${timestamp}.csv`,
        columnSeparator: ",",
        prependContent: buildReportCsvHeader({
          reportName: "Ticketing Item Report",
          searchString: location.search,
        }),
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Ticketing Item Report" />
        <ReportFilters
          includeSubEvents
          includeDates
          includeTime
          includeProductTypes
          productTypeLabel="Item Category"
          productTypeOptions={categoryOptions}
          groupingOptions={displayTypeOptions}
          groupingValue={displayType}
          onGroupingChange={(value) => setDisplayType(value as DisplayType)}
          customQueryPaths={{
            dateFrom: "_and[0].transaction_hour",
            dateTo: "_and[1].transaction_hour",
          }}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <div>
            <CustomSelect
              options={displayTypeOptions}
              defaultValue={displayType}
              onChange={(value) => setDisplayType(value as DisplayType)}
              title="Grouping"
            />
          </div>
          <div></div>
          <div className="flex items-end justify-end">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
          </div>
        </div>
      </div>
      {!isPivot && (
        <>
          <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap" }}>
            <ToggleButton
              isSelected={selectedDataKey === "total_net_sales"}
              onClick={() => handleDataKeyChange("total_net_sales")}
            >
              Total Net Sales
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "total_gross_sales"}
              onClick={() => handleDataKeyChange("total_gross_sales")}
            >
              Total Gross Sales
            </ToggleButton>
            <ToggleButton
              isSelected={selectedDataKey === "total_units_sold"}
              onClick={() => handleDataKeyChange("total_units_sold")}
            >
              Total Units Sold
            </ToggleButton>
          </div>
          <BarChartComponent rows={chartRows} selectedDataKey={selectedDataKey} displayType={displayType} />
        </>
      )}
      <div style={{ height: "60vh", marginTop: "4px" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            rowData={tableRows}
            columnDefs={currentColDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            paginationPageSize={100}
            gridOptions={gridOptions}
            pinnedBottomRowData={totalRow}
            onGridReady={(params) => (gridRef.current = params.api)}
          />
        </div>
      </div>
    </>
  );
};
